import React from "react"
import { graphql } from "gatsby"
import { rem } from "polished"
import { media } from "@sr/common"
import styled from "styled-components"
import { useSanity } from "@sr/common"
import {
  Page,
  Seo,
  SectionContentStrip,
  SectionIntroductionVideo,
  SectionCtaSection,
  App,
} from "@sr/gatsby-theme-ui"
import ProductEntryHero from "../components/Section/Products/ProductEntry/ProductEntryHero"
import ProductEntryProductHighlights from "../components/Section/Products/ProductEntry/ProductEntryProductHighlights"
import ProductEntryHowItWorks from "../components/Section/Products/ProductEntry/ProductEntryHowItWorks"
import ProductEntryFaq from "../components/Section/Products/ProductEntry/ProductEntryFaq"
import ProductEntryIntegrations from "../components/Section/Products/ProductEntry/ProductEntryIntegrations"
import ProductEntryDemo from "../components/Section/Products/ProductEntry/ProductEntryDemo"
import ProductEntryAdditionalResource from "../components/Section/Products/ProductEntry/ProductEntryAdditionalResource"
import ProductEntryRelatedProducts from "../components/Section/Products/ProductEntry/ProductEntryRelatedProducts"
import ProductEntryBenefits from "../components/Section/Products/ProductEntry/ProductEntryBenefits"
import ProductEntryHardware from "../components/Section/Products/ProductEntry/ProductEntryHardware"
import ProductEntryTestimonial from "../components/Section/Products/ProductEntry/ProductEntryTestimonial"
import ProductEntryFeatures from "../components/Section/Products/ProductEntry/ProductEntryFeatures"
import ProductEntryDisclaimer from "../components/Section/Products/ProductEntry/ProductEntryDisclaimer"
import ProductEntryKpis from "../components/Section/Products/ProductEntry/ProductEntryKPIs"
import CommunityWifiNav from "../components/Section/Products/CommunityWifi/CommunityWifiNav"

const TemplateProductEntry = ({ data, pageContext, location }: any) => {
  if (!data) return null
  const {
    breadcrumb: { crumbs },
  } = pageContext
  
  const currentPath = location.pathname

  const {
    additionalResources,
    benefits,
    contentStrip,
    cta,
    faq,
    hero,
    howItWorks,
    integrations,
    demo,
    _rawRelatedProducts,
    productHighlights,
    title,
    hardware,
    testimonial,
    icon,
    features,
    path,
    video,
    kpis,
    disclaimer,
  } = data.pageData
  
  let faqJson = {}
  const { body, header, introText } = contentStrip

  const ctaSectionButton = {
    color: "var(--color-primary)",
    externalHref: cta?.learnMoreLink?.linkInternal
      ? null
      : cta?.learnMoreLink?.learnMoreLinkExternal?.url,
    href: cta?.learnMoreLink?.linkInternal
      ? cta?.learnMoreLink?.learnMoreLinkInternal?.reference?.path?.current
      : null,
    blank: cta?.learnMoreLink?.learnMoreLinkExternal?.blank,
    text: "Learn More",
  }

  if (faq?.accordionArray?.length) {
    faqJson = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq?.accordionArray.map((item: any, _i: number) => {
        return {
          "@type": "Question",
          name: item.title,
          acceptedAnswer: {
            "@type": "Answer",
            text: item.body,
          },
        }
      }),
    }
  }


  return (
    <App crumbs={crumbs} location={location} crumbLabel={title}>
      {currentPath.includes("/community-wifi") ? <CommunityWifiNav /> : null}
      <Page>
        <TemplateProductEntryStyled>
          <ProductEntryHero hero={hero} productTitle={title} icon={icon} />
          <SectionContentStrip
            body={body}
            title={header}
            introText={introText}
            maxWidth={900}
            centerAligned={true}
          />
          {video?.displayVideo && (
            <SectionIntroductionVideo
              className="introduction-video"
              id="product-video"
              videoUrl={video?.url}
            />
          )}
          {disclaimer?.on && (
            <ProductEntryDisclaimer disclaimer={disclaimer?.disclaimer} />
          )}
          {kpis?.on && <ProductEntryKpis kpis={kpis} />}
          {cta?.on && (
            <SectionCtaSection
              on={cta?.on}
              ctaSectionTag={true}
              ctaSectionTagText={cta?.bannerTag}
              ctaSectionTitle={cta?.title}
              ctaSectionTextContent={cta?.body}
              ctaSectionSanityImage={cta?.img}
              ctaSectionButton={ctaSectionButton}
              marginTop={[104, 88]}
              marginBottom={[16, 0]}
            />
          )}
          <ProductEntryProductHighlights
            productHighlights={productHighlights}
          />
          <ProductEntryHowItWorks howItWorks={howItWorks} />
          <ProductEntryHardware hardware={hardware} />
          <ProductEntryBenefits benefits={benefits} path={path} />
          <ProductEntryTestimonial testimonial={testimonial} />
          <ProductEntryFeatures features={features} />
          <ProductEntryIntegrations integrations={integrations} />
          <ProductEntryDemo demo={demo} />
          <ProductEntryFaq faq={faq} />
          <ProductEntryRelatedProducts
            relatedProducts={_rawRelatedProducts}
            pageTitle={title}
          />
          <ProductEntryAdditionalResource
            additionalResource={additionalResources}
          />
        </TemplateProductEntryStyled>
      </Page>
      {faq?.accordionArray?.length ? (
        <script type="application/ld+json">{JSON.stringify(faqJson)}</script>
      ) : null}
    </App>
  )
}

export default TemplateProductEntry

export const Head = ({ data }: any) => {
  const { imgBuilder } = useSanity()
  const page = data?.pageData
  const seoData = page?.seo?.seo
  let metaArray = []

  const ogImage = seoData?.openGraph?.image?.asset

  if (ogImage) {
    const src = imgBuilder(ogImage)
      .width(1200)
      .height(627)
      .auto("format")
      .crop("focalpoint")
      .url()

    metaArray.push({
      property: "og:image",
      content: src,
    })
  }

  return seoData ? (
    <Seo
      title={seoData.metaTitle}
      noIndex={seoData?.nofollowAttributes}
      canonical={data?.pageData?.seo?.canonical}
      keywords={seoData.seoKeywords?.join(", ")}
      description={seoData.metaDescription}
      meta={metaArray}
    />
  ) : null
}

const TemplateProductEntryStyled = styled.article`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .introduction-video {
    margin: 0;
    margin-top: ${rem(40)};
    margin-bottom: ${rem(96)};

    @media ${media.midUp} {
      margin-top: ${rem(48)};
    }
  }

  .introduction-video__container {
    margin: 0;
  }
`

export const PRODUCT_ENTRY_QUERY = graphql`
  query ProductEntryQuery($path_context: String!) {
    pageData: sanityProductEntry(path: { current: { eq: $path_context } }) {
      path {
        current
      }
      seo {
        seo {
          metaTitle
          metaDescription
          seoKeywords
          nofollowAttributes
          openGraph {
            url
            image {
              asset {
                _id
              }
            }
          }
        }
      }
      icon
      title
      hero {
        img {
          alt
          ...SanityImageWithPreview
        }
        header
        intro
        primaryButtonText
        primaryButtonLink {
          ...SanityBuilderInternalLink
        }
        secondaryButtonText
        on
        secondaryButtonLinkType
        secondaryButtonInternalLink {
          ...SanityBuilderInternalLink
        }
        tourialId
      }
      video {
        displayVideo
        url
      }
      kpis {
        on
        header
        subHeader
        kpisList {
          statistic
          body
        }
        statisticsCopy
      }
      disclaimer {
        on
        disclaimer
      }
      contentStrip {
        introText
        header
        body
        on
      }
      cta {
        bannerTag
        body
        title
        on
        img {
          alt
          ...SanityImageWithPreview
        }
        learnMoreLink {
          linkInternal
          learnMoreLinkInternal {
            ...SanityBuilderInternalLink
          }
          learnMoreLinkExternal {
            url
            blank
          }
        }
      }
      features {
        header
        featuresList {
          svg {
            asset {
              url
            }
          }
          snippet
        }
      }
      productHighlights {
        img {
          alt
          ...SanityImageWithPreview
        }
        header
        body
        featureList {
          featureHeader
          body
        }
        on
      }
      howItWorks {
        header
        body
        on
        steps {
          step1Body
          step1Title
          step2Body
          step2Title
          step3Body
          step3Title
        }
      }
      hardware {
        on
        header
        _rawBody(resolveReferences: { maxDepth: 10 })
        ctaLink {
          ...SanityBuilderInternalLink
        }
        ctaText
        hardwareImgList {
          img {
            alt
            ...SanityImageWithPreview
          }
        }
      }
      benefits {
        header
        on
        owners {
          productEntryBenefitsOwnersBenefitsList {
            body
            title
          }
          productEntryBenefitsOwnersImg {
            alt
            ...SanityImageWithPreview
          }
          productEntryBenefitsEnableBtn
          productEntryBenefitsOwnersBtnText
          productEntryBenefitsOwnersBtnLink {
            ...SanityBuilderInternalLink
            anchor
          }
        }
        resident {
          productEntryBenefitsResidentBenefitsList {
            body
            title
          }
          productEntryBenefitsResidentImg {
            alt
            ...SanityImageWithPreview
          }
          productEntryBenefitsEnableBtn
          productEntryBenefitsResidentBtnText
          productEntryBenefitsResidentBtnLink {
            ...SanityBuilderInternalLink
            anchor
          }
        }
        siteTeam {
          productEntryBenefitsSiteTeamBenefitsList {
            body
            title
          }
          productEntryBenefitsSiteTeamImg {
            alt
            ...SanityImageWithPreview
          }
          productEntryBenefitsEnableBtn
          productEntryBenefitsSiteTeamBtnText
          productEntryBenefitsSiteTeamBtnLink {
            ...SanityBuilderInternalLink
            anchor
          }
        }
      }
      testimonial {
        quote
        name
        position
        company
        on
        readMore {
          path {
            current
          }
        }
      }
      integrations
      demo
      faq {
        header
        body
        on
        accordionArray {
          body
          title
        }
      }
      _rawRelatedProducts(resolveReferences: { maxDepth: 3 })
      additionalResources
    }
  }
`
